<div class="flex flex-col self-stretch gap-2.5"
     [formGroup]="certificateHolderForm"
>
  <div class="flex justify-between items-center">
    <h1 class="certificate-holder-headline">Certificate Holder ({{ manager.isReadOnly() ? 'View' : (manager.isEditMode() ? 'Edit' : 'Create') }} Mode)</h1>
    @if (!isMobile()) {
      <div class="flex justify-between items-center gap-2.5">
        <ng-container *ngTemplateOutlet="actionButtons"/>
      </div>
    }
  </div>

  @let companyNameControl = certificateHolderForm.get('companyName');
  <div>
    <p-iftalabel class="w-full">
      <input class="w-full p-inputtext"
             formControlName="companyName"
             id="companyName"
             pInputText
             placeholder="Enter Company Name"
             type="text"
             [attr.data-test]="'certificate-holder-company-name'"
             [invalid]="conditionFormElementValidity(companyNameControl)"
             [required]="true"
      />
      <label class="block font-medium mb-1"
             for="companyName"
      > Company Name
      </label>
    </p-iftalabel>
    @if (companyNameControl?.errors?.['required']) {
      <small class="block text-red-500 text-xs mt-1">Company Name is required.</small>
    }
    @if (certificateHolderForm.errors?.['certificateHolderExists'] && companyNameControl?.touched) {
      <small class="block text-red-500 text-xs mt-1">
        This combination of Company Name, Country and Freetext already exists.
      </small>
    }
  </div>

  @let countryControl = certificateHolderForm.get('country');
  <div>
    <albert-country-select
      controlName="country"
      inputId="country"
      label="Country"
      placeholder="Enter Country"
      [attr.data-test]="'certificate-holder-country'"
      [invalid]="conditionFormElementValidity(countryControl)"
      [required]="true"
    />

    @if (certificateHolderForm.errors?.['certificateHolderExists'] && countryControl?.touched) {
      <small class="block text-red-500 text-xs mt-1">
        This combination of Company Name, Country and Freetext already exists.
      </small>
    }
  </div>

  @let freetextControl = certificateHolderForm.get('freetext');
  <div>
    <p-iftalabel class="w-full">
      <textarea class="w-full"
                formControlName="freetext"
                id="freetext"
                pTextarea
                placeholder="Enter Freetext"
                rows="3"
                [attr.data-test]="'certificate-holder-freetext'"
                [autoResize]="false"
                [invalid]="conditionFormElementValidity(freetextControl)"
      ></textarea>
      <label for="freetext">Free Text</label>
    </p-iftalabel>
    @if (certificateHolderForm.errors?.['certificateHolderExists'] && freetextControl?.touched) {
      <small class="block text-red-500 text-xs mt-1">
        This combination of Company Name, Country and Freetext already exists.
      </small>
    }
  </div>
  <div class="w-full min-w-0"
       [formGroup]="linkedCertificatesFilterForm"
  >
    <div class="certificate-toolbar-inner flex min-w-0 gap-2 p-4 bg-white rounded-md border border-gray-300">
      <div class="filter-row flex w-full lg:w-1/3 min-w-0 justify-start gap-2">
        <albert-select class="w-full min-w-0"
                       controlName="period"
                       data-test="certificate-holder-details-period-select"
                       inputId="period"
                       label="Period"
                       [options]="periodOptions()"
        />
      </div>
    </div>
  </div>
  <p-divider align="left"
             type="solid"
  >
    Linked Certificates
  </p-divider>
  <div class="pl-4.75">
    @if (filteredUsedIn().length > 0) {
      <ul class="list-disc">
        @for (usedInControl of filteredUsedIn(); track usedInControl) {
          <li>
            <a class="underline"
               [attr.data-test]="'certificate-holder-linked-cert-' + usedInControl.uuid"
               [routerLink]="['/certificate', usedInControl.uuid]"
            >
              {{ usedInControl.uuid }}
            </a>
          </li>
        }
      </ul>
    } @else if (hasLinkedCertificates()) {
      <p class="font-medium">No linked certificates in this period</p>
    } @else {
      <p class="font-medium">Not used in any certificate</p>
    }
  </div>

  <p-divider align="left"
             type="solid"
  >Emails
  </p-divider>
  <div class="certificate-holder-emails gap-4 flex flex-col">
    @for (addressControl of certificateHolderForm.controls.recipientAddresses.controls; track $index) {
      <div class="certificate-holder-email flex flex-row gap-4">
        <div class="w-full">
          <p-iftalabel class="w-full">
            <input class="w-full p-inputtext"
                   pInputText
                   placeholder="Enter Email"
                   type="email"
                   [attr.data-test]="'certificate-holder-email-' + $index"
                   [disabled]="manager.isReadOnly()"
                   [id]="'email-' + $index"
                   [invalid]="addressControl.invalid && addressControl.touched"
                   [value]="addressControl.value.email"
                   (blur)="addressControl.markAsTouched()"
                   (input)="updateEmailValue(addressControl, $event)"
            />
            <label [for]="'email-' + $index">Email</label>
          </p-iftalabel>
          @if (addressControl.errors?.['email'] && addressControl.touched) {
            <small class="block text-red-500 text-xs mt-1"
                   [attr.data-test]="'certificate-holder-email-error-' + $index"
            >Please enter a valid email address.</small>
          }
          @if (addressControl.errors?.['duplicateEmail'] && addressControl.touched) {
            <small class="block text-red-500 text-xs mt-1"
                   [attr.data-test]="'certificate-holder-email-error-' + $index"
            >Email address already exists.</small>
          }
        </div>
        <span class="w-fit"
              pTooltip="Clear Email"
              tooltipPosition="left"
        >
          <button class="clear-email-btn"
                  iconOnly
                  pButton
                  severity="secondary"
                  type="button"
                  [disabled]="manager.isReadOnly()"
                  [outlined]="true"
                  (click)="removeEmail(addressControl)"
          >
            <i class="pi pi-times"></i>
          </button>
        </span>
      </div>
    }
  </div>
  <div class="flex justify-between items-center gap-2.5">
    <div>
      <button pButton
              type="button"
              [disabled]="manager.isReadOnly()"
              (click)="onAddEmail()"
      >
        Add Email
      </button>
    </div>
    <div class="flex justify-between items-center gap-2.5">
      @if (isMobile()) {
        <ng-container *ngTemplateOutlet="actionButtons"/>
      }
    </div>
  </div>
</div>

<ng-template #actionButtons>
  @if (manager.isViewMode()) {
    <button pButton
            severity="primary"
            type="button"
            [attr.data-test]="'certificate-holder-edit-button'"
            [disabled]="!permissionService.hasEditRights()"
            (click)="onEdit()"
    >
      Edit
    </button>
    <button pButton
            severity="secondary"
            type="button"
            [attr.data-test]="'certificate-holder-duplicate-button'"
            [disabled]="!permissionService.hasEditRights()"
            (click)="onDuplication()"
    >
      Duplicate
    </button>
  } @else {
    <button pButton
            severity="primary"
            type="button"
            [attr.data-test]="'certificate-holder-confirm-button'"
            [disabled]="certificateHolderForm.invalid"
            (click)="manager.saveCertificateHolder()"
    >
      Confirm
    </button>
    @if (manager.isEditMode()) {
      <button disabled="true"
              pButton
              severity="secondary"
              type="button"
              [attr.data-test]="'certificate-holder-delete-button'"
              [outlined]="true"
              (click)="manager.delete()"
      >
        Delete
      </button>
    }
    <button pButton
            severity="secondary"
            type="button"
            [attr.data-test]="'certificate-holder-cancel-button'"
            [outlined]="true"
            (click)="manager.cancel()"
    >
      Cancel
    </button>
  }
  @if (manager.isViewMode()) {
    <button pButton
            severity="secondary"
            type="button"
            [attr.data-test]="'certificate-holder-close-button'"
            (click)="onClose()"
    >
      Close
    </button>
  }
</ng-template>
